import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { LOGO } from "../lib/media";
import { useI18n } from "../i18n/LanguageContext";

const STORAGE_KEY = "ep_cookie_consent_v1";
const KEYS = ["necessary", "functional", "preferences", "analytics", "marketing"];
const ALL_ON = { necessary: true, functional: true, preferences: true, analytics: true, marketing: true };
const ALL_OFF = { necessary: true, functional: false, preferences: false, analytics: false, marketing: false };

function Toggle({ checked, disabled, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0 transition-colors duration-300 ${
        checked ? "bg-[#0e7c6b]" : "bg-black/15"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

export default function CookieConsent() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [prefs, setPrefs] = useState(ALL_ON);
  const cats = t("cookie.categories");

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) setVisible(true);
    else {
      try {
        setPrefs({ ...ALL_OFF, ...JSON.parse(stored).prefs });
      } catch {
        setVisible(true);
      }
    }
    const openHandler = () => {
      const s = localStorage.getItem(STORAGE_KEY);
      if (s) {
        try {
          setPrefs({ ...ALL_OFF, ...JSON.parse(s).prefs });
        } catch {}
      }
      setShowModal(true);
    };
    window.addEventListener("open-cookie-settings", openHandler);
    return () => window.removeEventListener("open-cookie-settings", openHandler);
  }, []);

  const persist = (p) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ prefs: p, ts: Date.now() }));
    setPrefs(p);
    setVisible(false);
    setShowModal(false);
  };

  return (
    <>
      <AnimatePresence>
        {visible && !showModal && (
          <motion.div
            data-testid="cookie-banner"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/35 p-5 backdrop-blur-md"
          >
            <motion.div
              initial={{ y: 28, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[86vh] w-full max-w-md overflow-y-auto rounded-[28px] bg-white px-7 py-10 text-center shadow-[0_40px_120px_rgba(0,0,0,0.25)] md:px-10"
            >
              <img src={LOGO.horizontalBlack} alt="Equestrian Partners" className="mx-auto h-6 w-auto object-contain" />
              <p className="mt-8 text-[15px] font-light leading-relaxed text-[#2a2b29]">
                {t("cookie.body")}{" "}
                <a href="/cookies" className="text-[#0e7c6b] underline underline-offset-2">
                  {t("cookie.policyLink")}
                </a>
                .
              </p>
              <div className="mt-9 space-y-3.5">
                <button
                  data-testid="cookie-accept-btn"
                  onClick={() => persist(ALL_ON)}
                  className="w-full rounded-full bg-[#050505] py-4 text-[15px] font-semibold text-white transition-opacity hover:opacity-85"
                >
                  {t("cookie.accept")}
                </button>
                <button
                  data-testid="cookie-customize-btn"
                  onClick={() => setShowModal(true)}
                  className="w-full rounded-full border border-[#050505]/25 py-4 text-[15px] font-medium text-[#050505] transition-colors hover:border-[#050505]"
                >
                  {t("cookie.customize")}
                </button>
                <button
                  data-testid="cookie-reject-btn"
                  onClick={() => persist(ALL_OFF)}
                  className="pt-2 text-sm font-light text-[#2a2b29] underline underline-offset-4 transition-colors hover:text-[#050505]"
                >
                  {t("cookie.reject")} →
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showModal && (
          <motion.div
            data-testid="cookie-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.96, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, y: 20, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-8 md:p-10"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="overline text-[#0e7c6b]">{t("cookie.prefsOverline")}</p>
                  <h3 className="font-display mt-3 text-2xl font-light tracking-tight text-[#050505]">
                    {t("cookie.modalTitle")}
                  </h3>
                </div>
                <button
                  data-testid="cookie-modal-close"
                  onClick={() => setShowModal(false)}
                  className="text-[#9a9a9a] transition-colors hover:text-[#050505]"
                >
                  <X size={22} />
                </button>
              </div>

              <div className="mt-8 space-y-6">
                {cats.map((c, i) => {
                  const key = KEYS[i];
                  const locked = key === "necessary";
                  return (
                    <div key={key} className="flex items-start justify-between gap-6">
                      <div>
                        <p className="text-sm font-medium text-[#050505]">{c.title}</p>
                        <p className="mt-1 text-sm font-light leading-relaxed text-[#4a4a4a]">{c.desc}</p>
                      </div>
                      <Toggle
                        checked={locked ? true : prefs[key]}
                        disabled={locked}
                        onChange={(v) => setPrefs((p) => ({ ...p, [key]: v }))}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <button
                  data-testid="cookie-modal-reject"
                  onClick={() => persist(ALL_OFF)}
                  className="ep-btn ep-btn-ghost flex-1 !text-sm"
                >
                  {t("cookie.reject")}
                </button>
                <button
                  data-testid="cookie-modal-save"
                  onClick={() => persist(prefs)}
                  className="ep-btn ep-btn-ghost flex-1 !text-sm"
                >
                  {t("cookie.save")}
                </button>
                <button
                  data-testid="cookie-modal-accept"
                  onClick={() => persist(ALL_ON)}
                  className="ep-btn ep-btn-dark flex-1 !text-sm"
                >
                  {t("cookie.accept")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
